"use client";

import React, { useEffect } from "react";
import Table from "@core/components/table";
import { useTanStackTable } from "@core/components/table/custom/use-TanStack-Table";
import Filters from "./filters";
import { campaignListColumns } from "./columns";
import TablePagination from "@core/components/table/pagination";
import TableFooter from "@core/components/table/footer";
import { exportToCSV } from "@core/utils/export-to-csv";
import { Campaign } from "packages/app/src/types";
import { deleteCampaign } from "../../../api/delete-campaign";

interface CampaignTableProps {
  campaigns: any[];
  filter?: string;
  isLoading: boolean;
  error: Error | null;
}

export default function PaymentLinkTable({
  campaigns,
  filter,
  isLoading,
  error,
}: CampaignTableProps) {
  if (isLoading) {
    return (
      <div className="w-full h-60 flex justify-center items-center text-[16px] font-semibold">
        Loading payment links…
      </div>
    );
  }

  // if there was an error fetching, show it
  if (error) {
    return (
      <div className="text-red-600 p-4">
        Error loading payment links: {error.message}
      </div>
    );
  }

  const { table, setData } = useTanStackTable<any>({
    tableData: campaigns,
    columnConfig: campaignListColumns,
    options: {
      initialState: {
        pagination: { pageIndex: 0, pageSize: 10 },
        columnOrder: [
          "_id",
          "title",
          "description",
          "amountNeeded",
          "availableBalance",
          "slug",
          "price",
          "status",
          "transactions",
          "paymentType",
          "createdAt",

        ],
      },
      meta: {
        handleDeleteRow: async (row) => {
          await deleteCampaign(row._id);
          setData((prev) => prev.filter((c) => c._id !== row._id));
        },
        handleMultipleDelete: async (rows) => {
          for (const r of rows) {
            await deleteCampaign((r as any)._id);
          }
          setData((prev) =>
            prev.filter(
              (c) => !rows.some((r: any) => (r as any)._id === c._id)
            )
          );
        },
      },
      enableColumnResizing: false,
    },
  });

  const selected = table.getSelectedRowModel().rows.map((r) => r.original);

  const handleExport = () => {
    exportToCSV(
      selected,
      "Payment ID,Title,Description,Amount Needed,Available Balance,Slug,Status,Transactions,Payment Type,Created On,actions",
      `paymentLinks_export_${selected.length}`
    );
  };

  useEffect(() => {
    table.setColumnOrder([
      "_id",
      "title",
      "description",
      "amountNeeded",
      "availableBalance",
      "slug",
      "price",
      "status",
      "transactions",
      "paymentType",
      "createdAt",
    ]);
  }, [table]);

  return (
    <>
      <Filters table={table} />

      <Table
        table={table}
        variant="modern"
        classNames={{
          container: "border border-muted rounded-md",
          rowClassName: "last:border-0",
        }}
      />

      <TableFooter table={table} onExport={handleExport} />
      <TablePagination table={table} className="py-4" />
    </>
  );
}
